/**
 * The left rail: the brain's state as the chain reports it, said in the brain's
 * own terms.
 *
 * Nothing here is invented. The tick, the spikes, the synapses, the readout of
 * the four neurons the event carries, the sensory poke and the state root are
 * the fields of the latest `BrainState` event - the same event the stage fires
 * its neurons from. The field summary is computed from the kernel's own
 * `potentialsRange` read (the i16 membrane field the indexer relays as
 * `/api/pot.bin`), against the kernel's own trigger: a neuron fires when its
 * charge reaches `threshold` (200.0 in the deployed brain's units), so "over the
 * line" is the chain's number, not a threshold this file chose.
 *
 * The rail is also the receipt of a feed. The press paints the poke at once, and
 * the thought the burn opened rolls the numbers, flashes the state root and says
 * what moved. A quiet rail therefore means a quiet chain - the brain thinks when
 * it is fed - never a missing read.
 */

import type { BrainInfo, Feed, Tick } from "./api";

import { isThought } from "./api";
import { t, type TKey } from "./i18n";

/** The kernel carries charge in 64ths (Q6), so what a human reads is `v / 64`. */
const Q6 = 64;

/**
 * What each burn tier writes into one sensory channel: the kernel multipliers of
 * `src/Obrain.sol` `levels`, indexed by the `tier` field of the `Feed` event.
 */
const DEPTHS = [31, 127, 255];

/** How long the rail stays marked as freshly thought. */
const FRESH_MS = 2_200;

/** How long the values take to roll from the previous thought's numbers. */
const ROLL_MS = 600;

/** Where the rail remembers which of its two readings the visitor was on. */
const TAB_KEY = "od-obrain-rail-tab";

/** The remembered reading, or null when there is none or storage refuses. */
function readStoredTab(): "state" | "log" | null {
  try {
    const stored = window.localStorage.getItem(TAB_KEY);

    return stored === "log" || stored === "state" ? stored : null;
  } catch {
    return null;
  }
}

/** How many thoughts the log keeps: more than a screen, and the list scrolls. */
const LOG_ROWS = 24;

/** How many fired neurons an open record names before it stops listing them. */
const FIRED_SHOWN = 8;

/** What the field holds, read from the kernel's own membrane potentials. */
export type FieldFacts = {
  /** Cells holding any charge at all. */
  readonly charged: number;
  /** Cells that gained charge since the previous read. */
  readonly gained: number;
  /** The largest charge in the field, in Q6 - the cell closest to firing. */
  readonly peak: number;
  /** Cells already at the trigger line. */
  readonly above: number;
};

/**
 * Summarize one membrane field against the kernel's trigger.
 *
 * The field is signed, and the kernel fires on the magnitude, so every count
 * here is over `|v|`: a neuron pushed to -17,000 spikes exactly like one pushed
 * to +17,000, and a rail that counted only positive charge would under-report
 * the brain.
 */
export function fieldFacts(
  current: Int16Array,
  previous: Int16Array | null,
  threshold: number,
): FieldFacts {
  let charged = 0;
  let gained = 0;
  let peak = 0;
  let above = 0;

  for (let neuron = 0; neuron < current.length; neuron += 1) {
    const value = current[neuron];
    const magnitude = value < 0 ? -value : value;

    if (magnitude > 0) charged += 1;

    if (magnitude > peak) peak = magnitude;

    if (magnitude >= threshold) above += 1;

    if (previous !== null && neuron < previous.length) {
      const was = previous[neuron];
      const before = was < 0 ? -was : was;

      if (magnitude > before) gained += 1;
    }
  }

  return { charged, gained, peak, above };
}

/** The charge one tier writes into a channel; null for a tier the kernel has no level for. */
export function depthOf(tier: number): number | null {
  return tier >= 0 && tier < DEPTHS.length ? DEPTHS[tier] : null;
}

/** A charge in Q6 as the number of synapses it stands for, one decimal. */
export function charge(q6: number): string {
  return (q6 / Q6).toFixed(1);
}

/** `0x7618…e6ac`: a hash or a poke the rail has no room to print whole. */
export function short(hex: string): string {
  if (!hex.startsWith("0x") || hex.length < 12) return hex === "" ? "-" : hex;

  return `${hex.slice(0, 6)}…${hex.slice(-4)}`;
}

/**
 * The thoughts the log offers, newest first.
 *
 * The window the indexer serves mixes the brain's own thoughts with its
 * per-block activity rows, whose `tick` is a block height; only a thought may
 * enter the log, and a thought the stream already showed is not shown twice.
 */
export function recentThoughts(ticks: readonly Tick[], cap: number = LOG_ROWS): Tick[] {
  const seen = new Set<string>();
  const rows: Tick[] = [];

  for (let at = ticks.length - 1; at >= 0 && rows.length < cap; at -= 1) {
    const tick = ticks[at];

    if (!isThought(tick) || seen.has(tick.txHash)) continue;

    seen.add(tick.txHash);
    rows.push(tick);
  }

  return rows;
}

/** One line of an open record: what the field is called, and what it held. */
export type StateField = { readonly label: TKey; readonly value: string };

/**
 * The `BrainState` event of one thought, field by field.
 *
 * Every field of the event is here, in the event's own order, so a visitor
 * reading the record is reading what the kernel emitted: the indexed tick, the
 * readout of neurons 0..3, the counts, whether the spike cap was hit, the sensory
 * poke, and the root the whole field was folded into.
 */
export function stateFields(tick: Tick): readonly StateField[] {
  const ids = tick.fired.slice(0, FIRED_SHOWN).map((neuron) => fmt(neuron));

  const fired =
    tick.fired.length > FIRED_SHOWN
      ? `${ids.join(" ")} … +${tick.fired.length - FIRED_SHOWN}`
      : ids.join(" ");

  return [
    { label: "vit.dTick", value: fmt(tick.tick) },
    { label: "vit.dReadout", value: tick.readout.map((q6) => charge(q6)).join(" · ") },
    { label: "vit.dFired", value: fired === "" ? "-" : fired },
    {
      label: "vit.dSpiked",
      value: `${fmt(tick.spiked)} ${t("vit.dSpikedOf")}`,
    },
    { label: "vit.dSynapses", value: fmt(tick.synapses) },
    { label: "vit.dCapped", value: t(tick.capped ? "vit.dCappedYes" : "vit.dCappedNo") },
    // the poke is read back into what it says - which channel, how much charge -
    // rather than printed as a word nobody can decode by eye
    { label: "vit.dPoke", value: pokeValue(tick.input) },
    { label: "vit.dBlock", value: fmt(tick.block) },
  ];
}

/**
 * What a poke word says, read the way the kernel wrote it.
 *
 * `feed()` hands the kernel `levels[tier] << (channel * 3)`: an 8-bit charge at
 * the channel's own bit stride (`src/Obrain.sol` feed, `src/ImmortalFruitFlies.sol`
 * think). The three levels are 31, 127 and 255 - all odd - so the lowest set bit
 * is the stride's own start, and the byte above it is the charge. That makes the
 * word readable: a visitor sees the channel and the charge, not thirty-two bytes
 * of it. A zero word is a thought the kernel ran without being given one.
 */
export function decodePoke(
  poke: string,
): { readonly channel: number; readonly depth: number } | null {
  const hex = poke.startsWith("0x") ? poke.slice(2) : "";

  if (hex === "" || /[^0-9a-f]/i.test(hex)) return null;

  const value = BigInt(`0x${hex}`);

  if (value === 0n) return null;

  let start = 0;

  while (((value >> BigInt(start)) & 1n) === 0n) start += 1;

  return {
    channel: Math.trunc(start / 3),
    depth: Number((value >> BigInt(start)) & 0xffn),
  };
}

/** The sensory poke, said as the channel and the charge it carried. */
export function pokeValue(poke: string): string {
  const decoded = decodePoke(poke);

  if (decoded === null) return t("vit.dPokeNone");

  return t("vit.dPokeValue", { c: String(decoded.channel), d: String(decoded.depth) });
}

/** The label under a log row: what that thought did, in six words. */
export function rowMeta(tick: Tick): string {
  return t("vit.logMeta", { fired: fmt(tick.spiked), synapses: fmt(tick.synapses) });
}

function fmt(n: number): string {
  return n.toLocaleString("en-US");
}

function element(id: string): HTMLElement | null {
  return document.getElementById(id);
}

/** Write a value, animating it up from what the rail showed before. */
function roll(el: HTMLElement | null, from: number, to: number): void {
  if (el === null) return;

  const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  if (still || from === to || from <= 0) {
    el.textContent = fmt(to);

    return;
  }

  const began = performance.now();

  const step = (now: number): void => {
    const through = Math.min((now - began) / ROLL_MS, 1);
    // the count arrives fast and settles: an eased, not linear, read
    const eased = 1 - (1 - through) ** 3;

    el.textContent = fmt(Math.round(from + (to - from) * eased));

    if (through < 1) window.requestAnimationFrame(step);
  };

  window.requestAnimationFrame(step);
}

export type VitalsPanel = {
  /** The window's own thoughts, newest first: the log's first filling. */
  readonly setHistory: (ticks: readonly Tick[]) => void;
  readonly updateBrain: (brain: BrainInfo | null) => void;
  readonly setField: (
    current: Int16Array | null,
    previous: Int16Array | null,
    threshold: number,
  ) => void;
  /** A thought landed: `mine` when the visitor's own burn opened it. */
  readonly thought: (tick: Tick, mine: boolean) => void;
  /** A burn was seen onchain: the sensory poke it paid for. */
  readonly poke: (burn: Pick<Feed, "channel" | "tier">, mine: boolean) => void;
  /** Nothing to report yet: says so instead of showing a hollow rail. */
  readonly idle: () => void;
  readonly relanguage: () => void;
};

export type VitalsInput = {
  /** Where a transaction opens; the rail asks each time, never at mount. */
  readonly explorer: () => string;
};

export function mountVitals(input: VitalsInput): VitalsPanel {
  const tickEl = element("vitTick");
  const spikedEl = element("vitSpiked");
  const spikedSubEl = element("vitSpikedSub");
  const synapsesEl = element("vitSynapses");
  const synapsesSubEl = element("vitSynapsesSub");
  const chargedEl = element("vitCharged");
  const chargedSubEl = element("vitChargedSub");
  const peakEl = element("vitPeak");
  const peakBarEl = element("vitPeakBar");
  const peakSubEl = element("vitPeakSub");
  const aboveEl = element("vitAbove");
  const senseEl = element("vitSense");
  const senseDepthEl = element("vitSenseDepth");
  const senseSubEl = element("vitSenseSub");
  const senseBarEl = element("vitSenseBar");
  const rootEl = element("vitRoot");
  const rootSubEl = element("vitRootSub");
  const wiringEl = element("vitWiring");
  const sealEl = element("vitSeal");
  const noteEl = element("vitNote");
  const railEl = element("vitals-panel");
  const logEl = element("vitLog");
  const fieldZoneEl = element("vitZoneField");

  let brain: BrainInfo | null = null;
  let facts: FieldFacts | null = null;
  let trigger = 12_800;
  /** The population the rail reports against: the field's own length when it is read. */
  let neurons = 0;
  let tick = 0;
  let spiked = 0;
  let synapses = 0;
  let sense: { channel: number; depth: number } | null = null;
  /**
   * How many thoughts the rail has shown. The first one - the newest the window
   * held when the page opened - has nothing behind it to delta against, and the
   * language switch has to keep saying so rather than turn its line into a
   * comparison against a thought the visitor never saw.
   */
  let thoughtsShown = 0;
  let spikedDelta = 0;
  let synapseDelta = 0;
  let fresh = 0;

  const sub = (el: HTMLElement | null, text: string): void => {
    if (el !== null) el.textContent = text;
  };

  const renderSense = (): void => {
    if (sense === null) {
      if (senseEl !== null) senseEl.textContent = "-";

      if (senseDepthEl !== null) senseDepthEl.textContent = "-";

      if (senseBarEl !== null) senseBarEl.style.width = "0%";
      sub(senseSubEl, t("vit.senseNone"));

      return;
    }

    if (senseEl !== null) senseEl.textContent = String(sense.channel);

    if (senseDepthEl !== null) senseDepthEl.textContent = `${sense.depth}/255`;

    if (senseBarEl !== null) senseBarEl.style.width = `${Math.round((sense.depth / 255) * 100)}%`;

    const depth = sense.depth;

    sub(
      senseSubEl,
      depth < 64 ? t("vit.senseLight") : depth < 200 ? t("vit.senseMost") : t("vit.senseFires"),
    );
  };

  const renderField = (): void => {
    // the record's B9: until the node answers, the field zone says it is reading
    // - the values are dashes and the gauge is hatched, never a skeleton
    fieldZoneEl?.classList.toggle("is-reading", facts === null);

    if (facts === null) {
      if (chargedEl !== null) chargedEl.textContent = "-";

      if (peakEl !== null) peakEl.textContent = "-";

      if (aboveEl !== null) aboveEl.textContent = "-";
      sub(chargedSubEl, t("vit.fieldWaiting"));
      sub(peakSubEl, t("vit.peakSub", { trigger: charge(trigger) }));

      return;
    }

    if (chargedEl !== null) chargedEl.textContent = fmt(facts.charged);

    if (peakEl !== null) peakEl.textContent = charge(facts.peak);

    if (aboveEl !== null) aboveEl.textContent = fmt(facts.above);

    sub(chargedSubEl, t("vit.chargedSub", { total: fmt(neurons), gained: fmt(facts.gained) }));
    sub(peakSubEl, t("vit.peakSub", { trigger: charge(trigger) }));

    if (peakBarEl !== null) {
      const filled = trigger > 0 ? Math.min(facts.peak / trigger, 1) : 0;

      peakBarEl.style.width = `${(filled * 100).toFixed(1)}%`;
      // a full bar means cells are standing at the line right now, not that the
      // scale ran out: the over state is what the kernel is about to fire
      peakBarEl.classList.toggle("is-over", facts.peak >= trigger);
    }
  };

  /**
   * The line under the numbers, kept as a key rather than as a sentence: the
   * language switch has to re-say whatever the rail last said, and a stored
   * string would come back in the language it was written in.
   */
  let note: { key: TKey; params: Record<string, string> } | null = null;

  const setNote = (key: TKey, params?: Record<string, string>): void => {
    note = { key, params: params ?? {} };

    if (noteEl !== null) noteEl.textContent = t(key, note.params);
  };

  let freshTimer = 0;

  const pulse = (mine: boolean): void => {
    if (railEl === null) return;

    fresh += 1;

    const stamp = fresh;

    railEl.classList.add("is-thought");
    railEl.classList.toggle("is-mine", mine);
    window.clearTimeout(freshTimer);
    freshTimer = window.setTimeout(() => {
      if (stamp !== fresh) return;

      railEl.classList.remove("is-thought");
    }, FRESH_MS);
  };

  /** What the rail says under the numbers: whose fee opened the thought it shows. */
  const sayThought = (mine: boolean): void => {
    setNote(mine ? "vit.noteMine" : "vit.noteThought", {
      tick: fmt(tick),
      fired: fmt(spiked),
    });
  };

  const sayLastThought = (): void => {
    const first = thoughtsShown <= 1;
    // the population comes from the field, which is a slow read: before it lands
    // the line says what it can instead of claiming "of 0"
    const sized = neurons > 0;

    sub(
      spikedSubEl,
      sized
        ? t(first ? "vit.spikedSubFirst" : "vit.spikedSub", {
            total: fmt(neurons),
            delta: delta(spikedDelta),
          })
        : t(first ? "vit.spikedSubAlone" : "vit.spikedSubAloneBefore", {
            delta: delta(spikedDelta),
          }),
    );
    sub(
      synapsesSubEl,
      t(first ? "vit.synapsesSubFirst" : "vit.synapsesSub", { delta: delta(synapseDelta) }),
    );
  };

  // ---- the two readings: the state as it stands, and the log --------------

  const tabs: { name: "state" | "log"; button: HTMLElement | null; pane: HTMLElement | null }[] = [
    { name: "state", button: element("vitTabState"), pane: element("vitPaneState") },
    { name: "log", button: element("vitTabLog"), pane: element("vitPaneLog") },
  ];

  /**
   * Which reading the rail shows. The choice is remembered only for the session
   * in which it was made - storage that refuses is a browser that refuses, and
   * the rail still has to open on something.
   */
  const showTab = (name: "state" | "log"): void => {
    for (const tab of tabs) {
      const on = tab.name === name;

      tab.button?.classList.toggle("is-on", on);
      tab.button?.setAttribute("aria-selected", on ? "true" : "false");

      if (tab.pane !== null) tab.pane.hidden = !on;
    }

    try {
      window.localStorage.setItem(TAB_KEY, name);
    } catch {
      // nothing to remember the choice with: the rail still switched
    }
  };

  for (const tab of tabs) {
    tab.button?.addEventListener("click", () => showTab(tab.name));
  }

  // ---- the log: every thought the window holds, each one openable ----------

  let log: Tick[] = [];
  /** The transaction whose record is open; one at a time, and none by default. */
  let openTx: string | null = null;
  /** Where the visitor reads the log; the rail opens on a reading, not on an empty pane. */
  const wanted = readStoredTab();

  if (wanted !== null) showTab(wanted);

  const recordFor = (tick: Tick): HTMLElement => {
    const list = document.createElement("dl");

    list.className = "vit-record";

    for (const field of stateFields(tick)) {
      const row = document.createElement("div");
      const name = document.createElement("dt");
      const value = document.createElement("dd");

      row.className = "vit-d";
      name.textContent = t(field.label);
      value.textContent = field.value;
      row.append(name, value);
      list.append(row);
    }

    const base = input.explorer().replace(/\/$/, "");

    if (base !== "" && tick.txHash !== "") {
      const link = document.createElement("a");

      link.className = "vit-tx";
      link.href = `${base}/tx/${tick.txHash}`;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.dataset.testid = "vitals-tx";
      link.textContent = t("vit.dTx", { tx: short(tick.txHash) });
      list.append(link);
    }

    return list;
  };

  const renderLog = (): void => {
    if (logEl === null) return;

    if (log.length === 0) {
      const empty = document.createElement("p");

      empty.className = "vit-sub";
      empty.textContent = t("vit.logEmpty");
      logEl.replaceChildren(empty);

      return;
    }

    logEl.replaceChildren(
      ...log.map((tick) => {
        const item = document.createElement("div");
        const row = document.createElement("button");
        const number = document.createElement("span");
        const meta = document.createElement("span");
        const isOpen = openTx !== null && openTx === tick.txHash;

        item.className = isOpen ? "vit-log-item is-open" : "vit-log-item";
        row.type = "button";
        row.className = "vit-log-row";
        row.dataset.vitTx = tick.txHash;
        row.dataset.testid = "vitals-log-row";
        row.setAttribute("aria-expanded", isOpen ? "true" : "false");
        // the row shows a tick on the left and counts on the right; a reader
        // that says it in order would spell "tick 544 fired"
        row.setAttribute(
          "aria-label",
          t("vit.logRowLabel", {
            tick: fmt(tick.tick),
            fired: fmt(tick.spiked),
            synapses: fmt(tick.synapses),
          }),
        );
        number.className = "vit-log-tick";
        number.textContent = t("vit.logTick", { tick: fmt(tick.tick) });
        meta.className = "vit-log-meta";
        meta.textContent = rowMeta(tick);
        row.append(number, meta);
        item.append(row);

        if (isOpen) item.append(recordFor(tick));

        return item;
      }),
    );
  };

  logEl?.addEventListener("click", (event) => {
    const target =
      event.target instanceof HTMLElement ? event.target.closest("[data-vit-tx]") : null;

    const tx = target instanceof HTMLElement ? (target.dataset.vitTx ?? null) : null;

    if (tx === null) return;

    openTx = openTx === tx ? null : tx;
    renderLog();

    if (openTx === null) return;

    // the record opens in place: a row below the fold of a short rail is a
    // click that looks like nothing happened
    document
      .querySelector(`[data-vit-tx="${openTx}"]`)
      ?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  });

  // a stored choice that names the log is honored: the tab the visitor left on
  // is the tab the rail opens, and the log below is already filled by then
  renderField();
  renderSense();
  renderLog();

  // the rail is wired: the markup is static, so a click that lands before the
  // indexer's history arrives would reach a button with no listener yet
  railEl?.setAttribute("data-ready", "true");

  return {
    setHistory: (ticks: readonly Tick[]) => {
      log = recentThoughts(ticks);
      renderLog();
    },

    updateBrain: (next: BrainInfo | null) => {
      const first = brain === null;
      // A throttled Arc read arrives as a brain-shaped zero: read_brain_info maps
      // every failed eth_call to 0, so "0 cells, not sealed" is a failed read, and
      // painting it would report a sealed 169,088-cell brain as empty and open.
      // The rail keeps the last reading it trusts, and says what it is doing until
      // it has one.
      const read = next !== null && next.neurons > 0 && next.words > 0 ? next : null;

      if (read === null) {
        if (next !== null && brain === null) sub(sealEl, t("vit.reading"));

        if (note === null) setNote("vit.reading");

        return;
      }

      brain = read;

      if (neurons === 0) neurons = read.neurons;
      trigger = read.threshold || 12_800;
      tick = Math.max(tick, read.tick);

      if (tickEl !== null) tickEl.textContent = fmt(tick);

      if (wiringEl !== null) {
        wiringEl.textContent = t("vit.wiringValue", {
          cells: fmt(read.neurons),
          tapes: read.tapes === null ? "?" : fmt(read.tapes),
        });
      }

      sub(sealEl, t(read.isSealed ? "vit.sealSealed" : "vit.sealOpen"));

      if (rootEl !== null) rootEl.textContent = shortRoot(read.stateRoot);
      sub(rootSubEl, t("vit.rootSub", { tick: fmt(tick) }));
      renderField();

      // the rail's own first paint is the chain's answer, not a placeholder
      if (first && tick > 0) sayThought(false);
    },

    setField: (current: Int16Array | null, previous: Int16Array | null, threshold: number) => {
      trigger = threshold || trigger;

      if (current === null) {
        facts = null;
        renderField();

        return;
      }

      facts = fieldFacts(current, previous, trigger);
      // the field is the population: a throttled brain read answers 0 cells, and
      // "of 0" under a live field would be a number the rail made up
      neurons = current.length;
      renderField();
    },

    thought: (next: Tick, mine: boolean) => {
      const previousSpiked = spiked;
      const previousSynapses = synapses;

      tick = Math.max(tick, next.tick);
      spiked = next.spiked;
      synapses = next.synapses;
      // a delta needs a thought behind it: the first one the rail shows is the
      // newest the window held, and nothing the visitor saw came before it
      const hadPrevious = thoughtsShown >= 1;

      spikedDelta = hadPrevious ? spiked - previousSpiked : 0;
      synapseDelta = hadPrevious ? synapses - previousSynapses : 0;

      if (tickEl !== null) tickEl.textContent = fmt(tick);
      roll(spikedEl, previousSpiked, spiked);
      roll(synapsesEl, previousSynapses, synapses);
      thoughtsShown += 1;
      sayLastThought();
      log = [next, ...log.filter((row) => row.txHash !== next.txHash)].slice(0, LOG_ROWS);
      renderLog();

      if (rootEl !== null) {
        rootEl.textContent = shortRoot(next.stateRoot);
        rootEl.classList.remove("is-changed");
        // restart the flash when a thought lands on the heel of another
        void rootEl.offsetWidth;
        rootEl.classList.add("is-changed");
      }

      sub(rootSubEl, t("vit.rootSub", { tick: fmt(tick) }));
      sayThought(mine);
      pulse(mine);
    },

    poke: (burn: Pick<Feed, "channel" | "tier">, mine: boolean) => {
      const depth = depthOf(burn.tier);

      if (depth === null) return;

      sense = { channel: burn.channel, depth };
      renderSense();

      if (senseBarEl !== null) {
        // the channel is being charged right now: the bar arrives, then settles
        senseBarEl.classList.remove("is-pouring");
        void senseBarEl.offsetWidth;
        senseBarEl.classList.add("is-pouring");
      }

      if (railEl !== null) railEl.classList.toggle("is-mine", mine);

      if (mine) setNote("vit.notePoke", { c: String(burn.channel), d: String(depth) });
    },

    relanguage: () => {
      renderField();
      renderSense();

      if (brain !== null) {
        if (wiringEl !== null) {
          wiringEl.textContent = t("vit.wiringValue", {
            cells: fmt(brain.neurons),
            tapes: brain.tapes === null ? "?" : fmt(brain.tapes),
          });
        }

        sub(sealEl, t(brain.isSealed ? "vit.sealSealed" : "vit.sealOpen"));
      } else {
        sub(sealEl, t("vit.reading"));
      }

      sayLastThought();
      sub(rootSubEl, t("vit.rootSub", { tick: fmt(tick) }));

      if (note !== null) {
        if (noteEl !== null) noteEl.textContent = t(note.key, note.params);
      }

      renderLog();
    },

    idle: () => {
      // a chain that has not thought since the window opened is a quiet chain,
      // and saying so is the honest read: the brain thinks when it is fed
      if (thoughtsShown === 0) setNote("vit.quiet");
    },
  };
}

/** `0x20ce…d288`: the whole field in eight characters, as the tape shows it. */
function shortRoot(root: string): string {
  if (!root.startsWith("0x") || root.length < 12) return root === "" ? "-" : root;

  return `${root.slice(0, 6)}…${root.slice(-4)}`;
}

function delta(change: number): string {
  return change > 0 ? `+${fmt(change)}` : fmt(change);
}
